import { ApiError } from "@/api/client";
import { isStaleBuildError } from "@/lib/stale-build";

/**
 * The two stacks report validation failures differently, and the admin panel
 * talks to whichever one is deployed:
 *
 *   Express (production today)  422 `VALIDATION_ERROR`, message "Validation
 *                               failed", real detail in
 *                               `details.validationErrors[]`
 *   NestJS                      400, no `code`, class-validator messages
 *                               already joined into `message`
 *
 * Showing the Express `message` verbatim would tell a user only "Validation
 * failed", so pull the field messages out when they are there.
 */
export function extractValidationMessage(error: ApiError): string | null {
  const raw = error.details?.["validationErrors"];
  if (!Array.isArray(raw)) return null;

  const messages = raw
    .map((entry) =>
      typeof entry === "object" && entry !== null
        ? (entry as { message?: unknown }).message
        : null,
    )
    .filter(
      (message): message is string => typeof message === "string" && message !== "",
    );

  return messages.length > 0 ? messages.join(". ") : null;
}

/**
 * Turns a thrown value into a sentence an admin can act on.
 *
 * One place for error prose, so the same failure reads the same way on every
 * screen. The skill's rule is that an error view says what failed and what to
 * do — never a raw error object, and never a status code on its own.
 *
 * Screens with a more specific story to tell (login, for instance) branch on
 * `ApiError.code` first and fall back to this.
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isNetworkError) {
      return "Couldn't reach the server. Check your connection and try again.";
    }
    if (error.status === 403) {
      return "You don't have permission to do that.";
    }
    if (error.status === 404) {
      return "We couldn't find that.";
    }
    if (error.status === 429) {
      return "Too many requests. Please wait a moment and try again.";
    }
    if (error.status >= 500) {
      return "The server ran into a problem. Please try again in a moment.";
    }
    // 422 from the Express stack carries the useful text one level down.
    const validation = extractValidationMessage(error);
    if (validation !== null) return validation;
    // Other 4xx messages from the API are written for humans already.
    return error.message;
  }
  // Reached only when the automatic reload was refused — see lib/stale-build.
  if (isStaleBuildError(error)) {
    return "Check your connection and try again.";
  }
  if (error instanceof Error && error.message !== "") return error.message;
  return "Something went wrong.";
}
